import { SEO_ROUTES, type CountryCode } from '@handyhand/i18n';

/**
 * Static (non-parametrized) route paths, resolved from the runtime country.
 *
 * Use `buildStaticPaths(country)` to materialize the table in route factories
 * (see `app.routes.ts`, `app.routes.server.ts`). Consumers that need the
 * current-country paths via DI can `inject(StaticPathsToken)` (see
 * `env.providers.ts`).
 *
 * Source-of-truth note: paths that are stable across the SEO + web codebases
 * (terms, blog, etc.) come from `@handyhand/i18n`. The `localOnly`
 * table only holds slugs that this SEO project owns exclusively — pages that
 * have no equivalent in handyhand-web.
 */

export type StaticPaths = ReturnType<typeof buildStaticPaths>;

const localOnly = {
  dk: {
    updates: 'opdateringer',
    deleteAccountChild: 'sletning-af-konto',
    dpiaChild: 'konsekvensanalyse',
    checklistSlug: 'checklists/:slug',
    tipsIndex: 'tips/index',
    search: 'search',
    contentAuthorSaxo: 'skribent/saxo',
    securityIncident: 'security/2026-11-01-fake-notification',
  },
  // NL's `index/inspiratie-voor-klussen-in-huis` inspiration index page is
  // intentionally no longer routed: retiring `staticPaths.inspirationIndex`
  // removed the route for both markets (the `/i` retirement covers DK only,
  // but the index route itself was shared). NL impact is negligible — the
  // page is in no NL sitemap and rendered an empty list — so no NL-side
  // redirect/410 was added; the page will simply 404 going forward.
  nl: {
    updates: 'updates',
    deleteAccountChild: 'account-verwijderen',
    dpiaChild: 'dpia',
    checklistSlug: 'checklists/:slug',
    tipsIndex: 'tips/index',
    search: 'zoeken',
    contentAuthorSaxo: 'auteur/saxo',
    securityIncident: 'security/2026-11-01-fake-notification',
  },
} as const;

export function buildStaticPaths(country: CountryCode) {
  const seo = <N extends keyof typeof SEO_ROUTES>(n: N): string =>
    SEO_ROUTES[n][country];

  if (!(country in localOnly)) {
    throw new Error(
      `buildStaticPaths: no local slug extras for country ${String(country)}`,
    );
  }
  const extras = localOnly[country as keyof typeof localOnly];
  const termsAndAgreements = seo('terms-and-agreements');
  const privacyPolicy = seo('privacy-policy');

  return {
    landing: seo('landing'),
    privacyPolicy,
    termsAndAgreements,
    aboutUs: seo('about-us'),
    howItWorks: seo('how-it-works'),
    press: seo('press'),
    calculateIncome: seo('calculate-income'),
    categories: seo('categories'),
    damageCompensation: seo('damage-compensation'),
    priceGuides: seo('price-guides'),
    app: seo('app'),
    blog: seo('blog'),
    blogPost: seo('blog-post'),
    findWork: seo('find-work'),
    handyhanderLevels: seo('handyhander-levels'),
    deleteAccount: `${termsAndAgreements}/${extras.deleteAccountChild}`,
    // Public DPIA lives under the privacy policy; English-only, noindex (see DpiaComponent).
    dpia: `${privacyPolicy}/${extras.dpiaChild}`,
    ...extras,
  } as const;
}
